/**
 * Feld- und regelgenauer Vergleich zwischen dem aktuellen Hybrid-myCheck-Zustand des Editors und einem
 * vom Agenten erzeugten Vorschlagsdokument (`MyCheckProposal.document.manifest`).
 * Reine Diff-Logik, kein Backend- oder
 * Bot-Zugriff.
 *
 * Bewusst getrennt von `mycheck-diff.ts` (KI): Hybrid-Regeln liegen als eigene `WorkflowDefinition`s im
 * Manifest (`manifest.steps` + `trigger.config.hybridEditor`), nicht als `configJson`-Strings, und die
 * Regel-Identität ist hier immer `id`-basiert (Hybrid-Regeln bekommen beim Anlegen sofort eine echte
 * Server-`id`, es gibt kein Äquivalent zu KI-Editors lokalen Schlüsseln).
 */

import {MyCheckFieldChange} from './mycheck-diff';

export interface HybridProposalRule {
  id: string;
  name: string;
  description: string;
  shortCode?: string;
  allowedDocumentClasses?: string[];
  allowedFileTypes?: string[];
  tagValues?: Record<string, string>;
}

export interface HybridProposalDocument {
  name: string;
  description: string;
  /** Entspricht `manifest.trigger.config.hybridEditor` – Speicher-Format, wie `save()` es schreibt. */
  setConfig: Record<string, unknown>;
  rules: HybridProposalRule[];
}

export interface HybridRuleChanges {
  /** Sollte im Normalfall immer leer sein: ein Vorschlag darf keine Regeln anlegen (siehe Struktur-Guard
   *  in `HybridEditorComponent.loadAndApplyProposal`). Existiert hier nur zur Erkennung. */
  added: HybridProposalRule[];
  /** Sollte im Normalfall immer leer sein: ein Vorschlag darf keine Regeln löschen. */
  removed: HybridProposalRule[];
  /** Regel-`id` → geänderte Feld-Pfade dieser Regel, z. B. `tagValues`. */
  changed: Map<string, string[]>;
}

export interface HybridDiff {
  /** Pfad (`name`, `setConfig.kiModel`, …) → Vorher/Nachher auf Set-Ebene. */
  fieldChanges: Map<string, MyCheckFieldChange>;
  ruleChanges: HybridRuleChanges;
}

/**
 * Diese vier `setConfig`-Schlüssel tragen ausschließlich pro-Regel-Daten (keyed by rule id) und werden
 * deshalb NICHT im generischen Set-Feld-Diff verglichen, sondern ausschließlich über `ruleChanges`
 * sichtbar gemacht – sonst gäbe es doppelte, UI-technisch nicht sinnvoll darstellbare Pfade wie
 * `setConfig.ruleAllowedFileTypes`.
 */
const SET_CONFIG_RULE_KEYS = new Set(['ruleAllowedDocumentClasses', 'ruleAllowedFileTypes', 'ruleTagValues', 'ruleShortCodes']);

/**
 * Liest ein rohes Hybrid-Manifest (Server-Antwort oder `proposal.document.manifest`) in das schlanke
 * `HybridProposalDocument`-Format – dieselbe Lesart, die `HybridEditorComponent.loadRuleset()` inline
 * anwendet. Wird sowohl für den Vorschlag als auch (über `HybridEditorComponent.buildHybridDocumentSnapshot`)
 * für den aktuellen Zustand verwendet, damit beide Seiten garantiert dieselbe Form haben.
 */
export function extractHybridProposalDocument(manifest: any): HybridProposalDocument {
  const setConfig = manifest?.trigger?.config?.hybridEditor && typeof manifest.trigger.config.hybridEditor === 'object'
    ? manifest.trigger.config.hybridEditor
    : (manifest?.config ?? {});
  const ruleAllowedDocumentClasses = setConfig?.ruleAllowedDocumentClasses ?? {};
  const ruleAllowedFileTypes = setConfig?.ruleAllowedFileTypes ?? {};
  const ruleTagValues = setConfig?.ruleTagValues ?? {};
  const ruleShortCodes = setConfig?.ruleShortCodes ?? {};

  const rules: HybridProposalRule[] = (Array.isArray(manifest?.steps) ? manifest.steps : [])
    .map((step: any) => {
      const id = String(step?.nodeId ?? step?.workflowDefinitionId ?? '').trim();
      return {
        id,
        name: String(step?.inputs?.['__name'] ?? ''),
        description: String(step?.inputs?.['__description'] ?? ''),
        shortCode: step?.inputs?.['__shortCode'] ?? ruleShortCodes[id],
        allowedDocumentClasses: Array.isArray(ruleAllowedDocumentClasses[id]) ? ruleAllowedDocumentClasses[id] : [],
        allowedFileTypes: Array.isArray(ruleAllowedFileTypes[id]) ? ruleAllowedFileTypes[id] : [],
        tagValues: ruleTagValues[id] && typeof ruleTagValues[id] === 'object' ? ruleTagValues[id] : {},
      };
    })
    .filter((rule: HybridProposalRule) => !!rule.id);

  return {
    name: String(manifest?.name ?? ''),
    description: String(manifest?.description ?? ''),
    setConfig: setConfig ?? {},
    rules,
  };
}

/**
 * Vergleicht den aktuellen Editor-Zustand (`current`) mit dem Vorschlag (`proposed`) und liefert die
 * Abweichungen auf Set- und Regel-Ebene. Identische Dokumente ergeben einen leeren Diff.
 */
export function computeHybridDiff(current: HybridProposalDocument, proposed: HybridProposalDocument): HybridDiff {
  const fieldChanges = new Map<string, MyCheckFieldChange>();
  diffField('name', current.name, proposed.name, fieldChanges);
  diffField('description', current.description, proposed.description, fieldChanges);

  const setConfigKeys = new Set([
    ...Object.keys(current.setConfig ?? {}),
    ...Object.keys(proposed.setConfig ?? {}),
  ]);
  for (const key of setConfigKeys) {
    if (SET_CONFIG_RULE_KEYS.has(key)) continue;
    diffField(`setConfig.${key}`, current.setConfig?.[key], proposed.setConfig?.[key], fieldChanges);
  }

  return {fieldChanges, ruleChanges: diffRules(current.rules ?? [], proposed.rules ?? [])};
}

function diffField(path: string, before: unknown, after: unknown, into: Map<string, MyCheckFieldChange>): void {
  if (!deepEqual(before, after)) {
    into.set(path, {before, after});
  }
}

/** Regel-Identität ist ausschließlich `id`-basiert: Hybrid-Regeln bekommen beim Anlegen sofort eine
 *  echte Server-`id`, anders als bei KI gibt es keine lokal erzeugten Regeln ohne `id`. */
function diffRules(current: HybridProposalRule[], proposed: HybridProposalRule[]): HybridRuleChanges {
  const currentById = new Map(current.filter(rule => rule.id).map(rule => [rule.id, rule]));

  const matchedCurrentIds = new Set<string>();
  const added: HybridProposalRule[] = [];
  const changed = new Map<string, string[]>();

  for (const proposedRule of proposed) {
    if (!proposedRule.id) continue;
    const currentRule = currentById.get(proposedRule.id);
    if (!currentRule) {
      added.push(proposedRule);
      continue;
    }
    matchedCurrentIds.add(proposedRule.id);
    const changedPaths = diffRulePair(currentRule, proposedRule);
    if (changedPaths.length > 0) {
      changed.set(proposedRule.id, changedPaths);
    }
  }

  const removed = current.filter(rule => !!rule.id && !matchedCurrentIds.has(rule.id));

  return {added, removed, changed};
}

function diffRulePair(current: HybridProposalRule, proposed: HybridProposalRule): string[] {
  const paths: string[] = [];
  if (!deepEqual(current.name, proposed.name)) paths.push('name');
  if (!deepEqual(current.description, proposed.description)) paths.push('description');
  if (!deepEqual(current.shortCode, proposed.shortCode)) paths.push('shortCode');
  if (!deepEqual(current.allowedDocumentClasses, proposed.allowedDocumentClasses)) paths.push('allowedDocumentClasses');
  if (!deepEqual(current.allowedFileTypes, proposed.allowedFileTypes)) paths.push('allowedFileTypes');
  if (!deepEqual(current.tagValues, proposed.tagValues)) paths.push('tagValues');
  return paths;
}

/**
 * Kleine handgeschriebene Struktur-Gleichheit: Werte sind hier ausschließlich JSON-sichere Strings,
 * Zahlen, Booleans, Arrays und flache `Record<string, string>`-Maps – kein Bedarf für eine Bibliothek.
 * Bewusst dupliziert statt aus `mycheck-diff.ts` importiert (siehe Datei-Kommentar: getrennte
 * Diff-Strategien für KI und Hybrid, kein gemeinsames internes Modul).
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((value, index) => deepEqual(value, b[index]));
  }

  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
}
